import { ApprovalStatus, type TApprovalPhaseKey } from '@modules/approvals';
import type { TSparseItem } from '@modules/items';
import type { TProjectRootItemType } from '@modules/projects/types';
import type { TOrganizationShortcuts } from './shortcuts.types';
import type { TProjectStagesMock } from '@modules/mocks/projectStages/types';
import type { TProjectTemplateMock } from '@modules/mocks/projectTemplates/types';

type TOrganizationLicense = {
  FULL: number;
  READ: number;
  AI: number;
};

export type TStyleVariant = 'NONE' | 'PENTA' | 'HARPS' | 'CARLREINER' | 'VITROMED' | 'MEISINGER' | 'WOEHLK';

export enum ECustomStyleScope {
  BLOCK = 'BLOCK',
  INLINE = 'INLINE',
}

export enum DisabledFeature {
  APPLY_TEMPLATE = 'APPLY_TEMPLATE',
}

export type TCustomStyle = {
  id: string;
  label: string;
  scope: ECustomStyleScope;
};

export type TStylesConfig = {
  styleVariant: TStyleVariant;
  customStyles: TCustomStyle[];
};

export type TSparseDocumentImportConfig = {
  _id: string;
  label: string;
};

export type TEnabledFeatureSetting = {
  enabled: boolean;
};

export type TProjectsFeatureSetting = TEnabledFeatureSetting & {
  enabledRootItemTypes: TProjectRootItemType[];
};

export type TOrganizationFeatureSettings = {
  projects?: TProjectsFeatureSetting;
  impactAnalysis?: TEnabledFeatureSetting;
  templates?: TEnabledFeatureSetting;
  snapshots?: TEnabledFeatureSetting;
  templateBinding?: TEnabledFeatureSetting;
  approvalFunctions?: TEnabledFeatureSetting;
  literatures?: TEnabledFeatureSetting;
  changes?: TEnabledFeatureSetting;
  aiIndexing?: TEnabledFeatureSetting;
  aiQa?: TEnabledFeatureSetting;
  aiEdit?: TEnabledFeatureSetting;
  dotxAsTemplate?: TEnabledFeatureSetting;
  registrations?: TEnabledFeatureSetting;
  externalLink?: TEnabledFeatureSetting;
};

export type TOrganizationTerminology = {
  groundedQa?: {
    short?: string;
    long?: string;
  };
  welcomeSubtitle?: string;
};

export type TCustomGroup = {
  id: string;
  name: string;
};

export type TOrganizationFolders = {
  qm?: string;
  countries?: string;
  literature?: string;
  changeTemplates?: string;
  qmBlocks?: string;
};

export type TSparseOrganization = {
  _id: string;
  name: string;
  softwareVersion?: string;
  stylesConfig: TStylesConfig;
  folders: TOrganizationFolders;
  customFields: TCustomField[];
  documentImportConfigs: TSparseDocumentImportConfig[];
  approvalWorkflows: TApprovalWorkflow[];
  isSelfReferencesConfigured: boolean; // not all organizations use self-references
  isQualioConfigured: boolean;
  terminology: TOrganizationTerminology;
  mock: {
    disabledFeatures?: DisabledFeature[];
    importRestriction?: {
      fileNameRegex?: string;
      message?: string;
      whitelistedMails?: string[];
    };
    isH5RefEnabled: boolean; // demo-only feature flag
    isAiEnabled: boolean; // sales-demo feature flag
    contentAutomation?: {
      documents: string[];
      dataInconsistency?: {
        label: string;
        originalContent: string;
        libraryContent: string;
      };
    };
    submissionPackage?: {
      submission: string;
      targetUrl: string;
      jurisdiction: string;
      notifiedBody: string;
    };
    foldersModifiedToday?: string[];
    change?: {
      changes: {
        changeNumber: string;
        changeTitle: string;
        changeType: string;
        status: string;
        targetDate: string;
        owner: string;
      }[];
      startAiAssessment?: boolean;
      assessment?: {
        questionDelay?: number;
        completionDelay?: number;
        messages?: string[];
        continuationMessages?: string[];
        informationRequests?: {
          id: string;
          label: string;
          question: string;
          fieldType: 'singleChoice';
          options: string[];
          required: boolean;
        }[];
      };
      newData?: {
        status?: string;
        changeType?: string;
        changeSubtype?: string;
        impactLevel?: string;
        assessmentSummary?: string;
        changeScope?: {
          changed?: string[];
          unchanged?: string[];
        };
        assessmentBasis?: {
          informationProvided?: string[];
          sourcesReviewed?: {
            document?: string;
            title?: string;
            finding?: string;
          }[];
        };
        keyConclusions?: {
          title?: string;
          conclusion?: string;
          confidence?: string;
        }[];
        affectedAreas?: {
          area?: string;
          impact?: string;
          rationale?: string;
          documents?: string[];
        }[];
        reviewedNoChangeAreas?: {
          area?: string;
          conclusion?: string;
          rationale?: string;
        }[];
        safetyPerformanceImpact?:
          | string
          | {
              level?: string;
              summary?: string;
              potentialImpacts?: string[];
              requiredControls?: string[];
            };
        regulatoryImpact?:
          | string
          | {
              classification?: { conclusion?: string; reviewRequired?: boolean; rationale?: string };
              intendedPurpose?: { conclusion?: string; reviewRequired?: boolean; rationale?: string };
              commercialReference?: { conclusion?: string; reviewRequired?: boolean; rationale?: string };
              basicUdiDi?: { conclusion?: string; reviewRequired?: boolean; rationale?: string };
              declarationOfConformity?: { conclusion?: string; reviewRequired?: boolean; rationale?: string };
              notifiedBodyNotification?: { conclusion?: string; reviewRequired?: boolean; rationale?: string };
              overallConclusion?: string;
            };
        assumptions?: string[];
        evidenceGaps?: {
          id?: string;
          title?: string;
          severity?: string;
          blocks?: string;
          description?: string;
          requiredAction?: string;
        }[];
        implementationPlan?: {
          id?: string;
          activity: string;
          responsibleFunction: string;
          assigneeEmail?: string;
          expectedDeliverable?: string;
          aiSupportedOrManual?: string;
          dependency?: string;
          executionMode?: string;
          deliverable?: string;
          dependencies?: string[];
          completionCriterion?: string;
          status?: string;
          actionType?: 'MANUAL' | 'AI_DOCUMENT_UPDATE' | 'AI_ASSISTED' | 'HUMAN_APPROVAL';
          humanReviewRequired?: boolean;
          primaryDemoAction?: boolean;
          targetDocuments?: {
            _id?: string;
            document: string;
            documentNumber?: string;
            currentRevision?: string;
            proposedRevision?: string;
            updateSummary?: string;
            primary?: boolean;
            openAfterCompletion?: boolean;
            openActionLabel?: string;
          }[];
          implementationProposal?: {
            available?: boolean;
            initialState?: 'NOT_REQUESTED' | 'READY_FOR_REVIEW' | 'ACCEPTED';
            triggerLabel?: string;
            title?: string;
            summary?: string;
            sourceEvidence?: {
              source?: string;
              title?: string;
              finding?: string;
            }[];
            diffs?: {
              id?: string;
              format?: 'HTML';
              title?: string;
              document?: string;
              documentId?: string;
              section?: string;
              beforeHtml?: string;
              afterHtml?: string;
              diffHtml?: string;
              rationale?: string;
              confidence?: 'High' | 'Medium' | 'Low';
              humanReviewRequired?: boolean;
            }[];
            reviewGate?: {
              requireDiffOpened?: boolean;
              requireAllDiffsOpened?: boolean;
              requireConsent?: boolean;
              consentLabel?: string;
              consentInitiallyChecked?: boolean;
              acceptActionLabel?: string;
              acceptedStatus?: string;
            };
            completionMessage?: string;
          };
          manualControlNote?: string;
          blockerReason?: string;
        }[];
        traceabilityPreview?: {
          risk?: string;
          riskControl?: string;
          requirement?: string;
          verification?: string;
        }[];
      };
    };
    // Change management V2 prototype. `enabled` surfaces it in the nav; a confideck
    // developer can register templates with prefilled values that users pull in,
    // and seed the dashboard register via `changes`.
    changeV2?: {
      enabled?: boolean;
      // The organization-configured assessment template selected for the demo
      // Change, plus the pinned version (see the changeV2 assessmentTemplate model).
      selectedTemplateId?: string;
      selectedTemplateVersion?: number;
      // Config-driven assessment templates (sections, custom fields, questions,
      // candidate affected objects, preliminary activities). Typed as unknown[]
      // here to avoid a users -> changeV2 dependency cycle; the changeV2 store
      // validates and casts them to TAssessmentTemplateConfig at runtime.
      assessmentTemplates?: unknown[];
      // The instantiated assessment record(s) seeded onto the selected Change
      // (answers, custom-field values, section progress, affected objects,
      // decision summary). One object or an array. Cast at runtime.
      assessment?: unknown;
      // Seed entries for the company-wide change register shown on the V2
      // dashboard. When present, they replace the built-in demo seed data.
      changes?: {
        displayId?: string;
        title: string;
        status: string;
        changeManager?: string;
        initiator?: string;
        currentOwner?: string;
        scopeType?: string;
        criticality?: string;
        changeCategory?: string;
        progress?: number;
        requestDate?: string;
        targetDate?: string;
        nextAction?: string;
        affectedProducts?: string[];
        affectedProductFamilies?: string[];
        affectedQmsAreas?: string[];
        blockers?: string[];
        awaitingExternalDecision?: boolean;
      }[];
      templates?: {
        id: string;
        name: string;
        description: string;
        applicability: string;
        featured?: boolean;
        requestPrefills: {
          title?: string;
          description?: string;
          reasonOrTrigger?: string;
          desiredOutcome?: string;
          implementationProposal?: string;
          scopeType?: string;
          urgency?: string;
          affectedQmsAreas?: string[];
          changeCategory?: string;
        };
        assessmentSections: string[];
        suggestedRoles: string[];
        suggestedTasks: { title: string; assignedFunction: string; documents?: TSparseItem[] }[];
        suggestedAffectedObjectCategories: string[];
        suggestedWorkflowPolicy: string;
        surfaceWorkflowPolicy?: boolean;
      }[];
    };
    // Training & Competency Management prototype. `enabled` + `documentId` surface
    // the training requirement for the configured document; the rest prefill the
    // "Assign training" and "My Training" demo surfaces (the frontend supplies
    // built-in fallbacks when a field is omitted).
    training?: {
      enabled?: boolean;
      documentId?: string;
      documentName?: string;
      revision?: string;
      reason?: string;
      trainingMethod?: string;
      targetFunctions?: string[];
      dueInDays?: number;
      employees?: {
        id: string;
        name: string;
        function: string;
        included?: boolean;
      }[];
    };
    // Pre-submission review prototype. `enabled` surfaces it in the nav; `qm` and
    // each `products` entry hold the demo scenarios and register history of that
    // space, and `reviewBases` optionally replaces the built-in basis catalog.
    // Typed as unknown here to avoid a users -> review dependency cycle; the
    // review module validates and casts them at runtime.
    review?: {
      enabled?: boolean;
      reviewBases?: unknown[];
      qm?: unknown;
      products?: unknown[];
    };
    // A nonempty list makes New on the Projects page open the template picker.
    projectTemplates?: TProjectTemplateMock[];
    projectStages?: TProjectStagesMock[];
  };
  productCardAvatarCharacterCount: number;
  versioningConfig?: {
    changeNoteDefault?: string;
    acceptNoteDefault?: string;
    declineNoteDefault?: string;
    acceptNoteDefaultsByPhase?: Partial<Record<TApprovalPhaseKey, string>>;
    declineNoteDefaultsByPhase?: Partial<Record<TApprovalPhaseKey, string>>;
  };
  customGroups: TCustomGroup[];
  featureSettings: TOrganizationFeatureSettings;
  blockTags?: TBlockTag[];
  shortcuts?: TOrganizationShortcuts;
};

export type TBlockTag = {
  id: string;
  label: string;
  active: boolean;
};

export type TOrganization = Omit<TSparseOrganization, 'productCardAvatarCharacterCount'> & {
  productCardAvatarCharacterCount?: number;
  license: TOrganizationLicense;
};

// Reusable Custom Field Types
export type TCustomFieldOption = {
  id: string; // e.g. 'cf-option-1'
  label: string;
  active?: boolean;
};

export type TCustomFieldRaw =
  | { [ECustomFieldType.TEXT]: string }
  | { [ECustomFieldType.DROPDOWN]: string }
  | { [ECustomFieldType.DATE]: string };

export enum ECustomFieldType {
  TEXT = 'TEXT',
  DROPDOWN = 'DROPDOWN',
  DATE = 'DATE',
}

export enum ECustomFieldDisplayMode {
  ALWAYS_HIDE = 'ALWAYS_HIDE',
  HIDE_WHEN_EMPTY = 'HIDE_WHEN_EMPTY',
  ALWAYS_DISPLAY = 'ALWAYS_DISPLAY',
}

export enum ECustomFieldUniqueIdNamePosition {
  BEFORE = 'BEFORE',
  AFTER = 'AFTER',
}

export type TCustomFieldUniqueIdConfig = {
  enabled: boolean;
  digits: number;
  prefix: string;
  suffix: string;
  nameSeparator: string;
  namePosition: ECustomFieldUniqueIdNamePosition;
  autoGenerateOnImport?: {
    enableDocuments?: boolean;
    enableFiles?: boolean;
  };
};

export type TCustomField = {
  id: string; // e.g. 'cf-process-owner'
  label: string;
  type: ECustomFieldType;
  defaultValue: TCustomFieldRaw | null;
  options?: TCustomFieldOption[]; // only for DROPDOWN
  displayMode?: ECustomFieldDisplayMode; // not in use yet
  uniqueId?: TCustomFieldUniqueIdConfig;
  active: boolean;
};

export enum WorkflowPhase {
  AUTHORING = ApprovalStatus.AUTHORING,
  REVIEWING = ApprovalStatus.REVIEWING,
  APPROVING = ApprovalStatus.APPROVING,
}

export enum EConditionOperator {
  EQUALS = 'EQUALS',
  // NOT_EQUALS = 'NOT_EQUALS',
  // INCLUDES = 'INCLUDES',
  // GREATER_THAN = 'GREATER_THAN',
  // LESS_THAN = 'LESS_THAN',
}

export enum EConditionLogicalOperator {
  AND = 'AND',
  // OR = 'OR',
}

export type TCustomFieldCondition = {
  fieldId: string;
  operator: EConditionOperator;
  value: TCustomFieldRaw;
};

export type TApprovalRuleConditions = {
  operator: EConditionLogicalOperator;
  customFieldConditions?: TCustomFieldCondition[];
  // future: folderConditions?: TFolderCondition[];
  // future: templateConditions?: TTemplateCondition[];
};

export type TMinimumUsersRuleConfig = {
  phase: WorkflowPhase;
  minUsers: number;
  conditions?: TApprovalRuleConditions;
};

export type TUniqueUserRuleConfig = {
  phaseA: WorkflowPhase;
  phaseB: WorkflowPhase;
  exclusive?: boolean;
  conditions?: TApprovalRuleConditions;
};

export type TApprovalRules = {
  minimumUsers?: TMinimumUsersRuleConfig[];
  uniqueUsers?: TUniqueUserRuleConfig[];
};

export type TEnabledApprovalPhases = {
  [WorkflowPhase.AUTHORING]?: boolean;
  [WorkflowPhase.REVIEWING]?: boolean;
  [WorkflowPhase.APPROVING]?: boolean;
};

export type TApprovalWorkflow = {
  enabledPhases?: TEnabledApprovalPhases;
  minimumUsers?: TMinimumUsersRuleConfig[];
  uniqueUsers?: TUniqueUserRuleConfig[];
  conditions?: TApprovalRuleConditions;
};
